// Major Incident Command — the 15-minute clock page

import { pbAuthedFetch } from '../authHeaders';
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function ElapsedClock({ declaredAt }: { declaredAt: string }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const elapsed = now - new Date(declaredAt).getTime();
  const isOver = elapsed > 15 * 60 * 1000;

  return (
    <span
      style={{
        fontSize: 'var(--text-2xl)',
        fontWeight: 700,
        fontFamily: 'var(--mono)',
        color: isOver ? 'var(--risk)' : 'var(--navy)',
        letterSpacing: '0.02em',
      }}
    >
      {formatElapsed(elapsed)}
    </span>
  );
}

function NotificationClock({ incident }: { incident: any }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (incident.first_notification_sent_at) {
    const notifiedAt = new Date(incident.first_notification_sent_at);
    return (
      <span className="pill pill--ok">
        <i></i> Notified at {notifiedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    );
  }

  const elapsed = now - new Date(incident.declared_at).getTime();
  const isOver = elapsed > 15 * 60 * 1000;

  return (
    <span
      className={`pill ${isOver ? 'pill--risk' : 'pill--warn'}`}
    >
      <i></i>
      {isOver ? '⚠ OVER TARGET — ' : ''}
      {formatElapsed(elapsed)} / 15:00
    </span>
  );
}

export function MIMPage() {
  const queryClient = useQueryClient();
  const [selectedIncident, setSelectedIncident] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [audience, setAudience] = useState<'leadership' | 'technical' | 'all_staff'>('leadership');
  const [impactOverride, setImpactOverride] = useState('');
  const [workaroundOverride, setWorkaroundOverride] = useState('');
  const [statusOverride, setStatusOverride] = useState('');

  const { data: mimData, isLoading: mimLoading, error: mimError, refetch: refetchMim } = useQuery({
    queryKey: ['mim'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/mim');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: incidentDetail } = useQuery({
    queryKey: ['mim', selectedIncident],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/mim/${selectedIncident}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!selectedIncident,
  });

  const { data: performance } = useQuery({
    queryKey: ['mim-performance'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/mim/performance');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const sendNotification = useMutation({
    mutationFn: async ({ incidentId, body, audience }: { incidentId: string; body: string; audience: string }) => {
      const res = await pbAuthedFetch(`/api/mim/${incidentId}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, audience }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mim'] });
      queryClient.invalidateQueries({ queryKey: ['mim', selectedIncident] });
      setComposerOpen(false);
    },
  });

  const [bodyOverride, setBodyOverride] = useState<string | null>(null);

  const regenerateNotification = useMutation({
    mutationFn: async () => {
      const currentBody = bodyOverride ?? buildNotificationBody();
      const res = await pbAuthedFetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `Rewrite this incident leadership notification to be tighter and clearer for a 20-second read. Preserve every fact, number and name exactly — do not invent or drop anything:\n\n${currentBody}`,
        }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: (data) => {
      if (data.reply) setBodyOverride(data.reply);
    },
  });

  const updateIncidentMutation = useMutation({
    mutationFn: async ({ id, severity, state }: { id: string; severity?: string; state?: string }) => {
      const res = await pbAuthedFetch(`/api/mim/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ severity, state }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mim'] });
      queryClient.invalidateQueries({ queryKey: ['mim', selectedIncident] });
    },
  });

  const MIM_STATES = ['declared', 'active', 'monitoring', 'resolved', 'post_review'];
  const MIM_SEVERITIES = ['critical', 'high', 'medium', 'low'];

  // "Active Incidents Panel" below (and its "Active: N" count) is meant to
  // show only open incidents — resolved/post_review ones stay in the API
  // response (mim.js's GET / never filters by state) but were rendering
  // here indefinitely with a live countdown clock and no visual distinction
  // from a genuinely active incident.
  const incidents = (mimData?.incidents || []).filter(
    (i: any) => i.state !== 'resolved' && i.state !== 'post_review'
  );
  const detail = incidentDetail;
  const perfIncidents = performance?.incidents || [];
  const targetMinutes = 15;

  const activeIncident = selectedIncident
    ? (detail?.incident
        ? { ...incidents.find((i: any) => i.id === selectedIncident), ...detail.incident, timeline: detail.timeline, participating_groups: detail.participating_groups }
        : incidents.find((i: any) => i.id === selectedIncident) || null)
    : null;

  const buildNotificationBody = useCallback(() => {
    if (!activeIncident) return '';
    const detected = activeIncident.detected_at ? new Date(activeIncident.detected_at).toLocaleString() : '—';
    const declared = activeIncident.declared_at ? new Date(activeIncident.declared_at).toLocaleString() : '—';
    const elapsed = activeIncident.declared_at
      ? formatElapsed(Date.now() - new Date(activeIncident.declared_at).getTime())
      : '—';

    return [
      `WHAT: ${activeIncident.service_name || activeIncident.service || 'Service'} ${activeIncident.severity === 'critical' ? 'unavailable' : 'degraded'}`,
      `WHEN: Detected ${detected} · Declared ${declared} · ${elapsed} elapsed`,
      `WHO: ~${activeIncident.estimated_users_impacted ?? '?'} users · ${activeIncident.sites || '—'}`,
      `IMPACT: ${impactOverride || activeIncident.impact_statement || 'Assessing impact...'}`,
      `WORKAROUND: ${workaroundOverride || activeIncident.workaround || 'None identified yet'}`,
      `STATUS: ${statusOverride || activeIncident.current_status || 'Investigating'}`,
      `NEXT UPDATE: ${activeIncident.eta_next_update ? new Date(activeIncident.eta_next_update).toLocaleString() : 'TBD'}`,
      `BRIDGE: ${activeIncident.bridge_url || '—'}`,
      `COMMANDER: ${activeIncident.declared_by || '—'}`,
    ].join('\n');
  }, [activeIncident, impactOverride, workaroundOverride, statusOverride]);

  // Timeline computation
  const timelineEvents = activeIncident?.timeline || [];

  // Coordination: groups
  const groups = activeIncident?.participating_groups || [];
  const childTickets = activeIncident?.child_tickets || [];

  // Performance chart data
  const perfChartData = perfIncidents.slice(0, 20).map((inc: any) => ({
    name: inc.service || inc.id?.slice(0, 8) || '—',
    minutes: inc.time_to_first_notification_minutes ?? null,
    over: (inc.time_to_first_notification_minutes ?? 0) > targetMinutes,
  }));

  // Repeat incidents by service
  const repeatByService: Record<string, number> = {};
  for (const inc of perfIncidents) {
    const svc = inc.service || 'Unknown';
    repeatByService[svc] = (repeatByService[svc] || 0) + 1;
  }
  const repeatEntries = Object.entries(repeatByService)
    .filter(([, count]) => count > 1)
    .sort(([, a], [, b]) => b - a);

  // Loading state
  if (mimLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Incident Management</p>
              <h1 className="pagehead__title">Major Incident Command</h1>
              <div className="pagehead__facts">
                <span className="fact">15-minute notification clock</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading incident data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (mimError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Incident Management</p>
              <h1 className="pagehead__title">Major Incident Command</h1>
              <div className="pagehead__facts">
                <span className="fact">15-minute notification clock</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load incident data</h3>
                <p className="state__body">
                  Could not connect to the MIM API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchMim()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Incident Management</p>
            <h1 className="pagehead__title">Major Incident Command</h1>
            <div className="pagehead__facts">
              <span className="fact">15-minute notification clock</span>
              <span className="fact">Active: <b>{incidents.length}</b></span>
            </div>
          </div>
          <div className="box__head-actions">
            <button className="btn btn--secondary btn--sm" onClick={() => window.askStella?.('Summarize the active major incident and what still needs a decision.')}>Ask Stella about this</button>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Info notice */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">15-Minute Notification Target</p>
            <p className="notice__body">
              Every declared major incident must notify stakeholders within 15 minutes. The clock starts at declaration.
            </p>
          </div>
        </div>

        {/* Active Incidents Panel */}
        {incidents.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 'var(--sp-4)' }}>
            {incidents.map((incident: any) => (
              <div
                key={incident.id}
                className="box"
                style={{
                  cursor: 'pointer',
                  borderTopColor: selectedIncident === incident.id ? 'var(--gold)' : undefined,
                  borderRightColor: selectedIncident === incident.id ? 'var(--gold)' : undefined,
                  borderBottomColor: selectedIncident === incident.id ? 'var(--gold)' : undefined,
                  borderLeft: selectedIncident === incident.id
                    ? '4px solid var(--gold)'
                    : incident.first_notification_sent_at
                      ? '4px solid var(--ok)'
                      : '4px solid var(--risk)',
                }}
                onClick={() => setSelectedIncident(incident.id)}
              >
                <div className="box__head">
                  <div className="box__head-text">
                    <p className="box__eyebrow">{incident.site || '—'}</p>
                    <h2 className="box__title">{incident.service_name || incident.service || 'Unknown Service'}</h2>
                  </div>
                  <div className="box__head-actions">
                    <span className={`pill pill--${incident.severity === 'critical' ? 'risk' : 'warn'}`}>
                      <i></i> {incident.severity || 'high'}
                    </span>
                  </div>
                </div>
                <div className="box__body">
                  <div style={{ marginBottom: 'var(--sp-3)' }}>
                    <ElapsedClock declaredAt={incident.declared_at} />
                  </div>

                  <div style={{ marginBottom: 'var(--sp-2)' }}>
                    <NotificationClock incident={incident} />
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 'var(--space-1)', whiteSpace: 'nowrap' }}>
                      <span style={{ color: 'var(--ink-45)' }}>Users:</span>
                      <strong>~{incident.estimated_users_impacted ?? '?'}</strong>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 'var(--space-1)', whiteSpace: 'nowrap' }}>
                      <span style={{ color: 'var(--ink-45)' }}>Status:</span>
                      <strong>{incident.current_status || 'Investigating'}</strong>
                    </span>
                  </div>

                  {incident.bridge_url && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', marginBottom: 'var(--sp-2)' }}>
                      Bridge: <a href={incident.bridge_url} target="_blank" rel="noreferrer" style={{ fontFamily: 'var(--mono)' }}>{incident.bridge_url}</a>
                    </div>
                  )}
                </div>
                <div className="box__foot">
                  <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                    <button
                      className="btn btn--primary btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIncident(incident.id);
                        setBodyOverride(null);
                        setComposerOpen(true);
                      }}
                    >
                      Compose Notification
                    </button>
                    <button
                      className="btn btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIncident(incident.id);
                      }}
                    >
                      Add Group
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="box">
            <div className="box__body">
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No active major incidents</h3>
                <p className="state__body">
                  The 15-minute clock is quiet. All clear.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Coordination View */}
        {activeIncident && (
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Coordination</p>
                <h2 className="box__title">{activeIncident.service_name || activeIncident.service || 'Incident'}</h2>
              </div>
              <div className="box__head-actions">
                <button className="btn btn--ghost btn--sm" onClick={() => setSelectedIncident(null)}>
                  Close
                </button>
              </div>
            </div>
            <div className="box__body">
              {activeIncident ? (
                <div className="mim-editor" style={{ marginBottom: 'var(--sp-5)' }}>
                  <label>
                    Severity
                    <select
                      value={activeIncident.severity || ''}
                      disabled={updateIncidentMutation.isPending}
                      onChange={(e) => updateIncidentMutation.mutate({ id: activeIncident.id, severity: e.target.value })}
                    >
                      {MIM_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label>
                    State
                    <select
                      value={activeIncident.state || 'declared'}
                      disabled={updateIncidentMutation.isPending}
                      onChange={(e) => updateIncidentMutation.mutate({ id: activeIncident.id, state: e.target.value })}
                    >
                      {MIM_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  {updateIncidentMutation.isError ? <span className="mim-error">Update failed — try again.</span> : null}
                </div>
              ) : null}

              {/* Timeline */}
              <div style={{ marginBottom: 'var(--sp-5)' }}>
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-45)' }}>Incident Timeline</h4>
                {timelineEvents.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center' }}>
                    {timelineEvents.map((evt: any, idx: number) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                        <div
                          style={{
                            padding: 'var(--sp-2) var(--sp-3)',
                            background: 'var(--surface-sub)',
                            border: '1px solid var(--hair)',
                            borderRadius: 'var(--r-ctl)',
                            fontSize: 'var(--text-xs)',
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>{evt.label || evt.event_type}</div>
                          <div style={{ fontFamily: 'var(--mono)', color: 'var(--ink-45)', fontSize: 'var(--text-xs)' }}>
                            {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : '—'}
                          </div>
                        </div>
                        {idx < timelineEvents.length - 1 && (
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)' }}>
                            → {timelineEvents[idx + 1]?.timestamp && evt.timestamp
                              ? `${Math.round((new Date(timelineEvents[idx + 1].timestamp).getTime() - new Date(evt.timestamp).getTime()) / 60000)}m`
                              : ''}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {['Detection', 'Declaration', 'Bridge', 'Groups Joined', 'First Notification', 'Mitigation', 'Resolution'].map((step, idx) => (
                      <div
                        key={step}
                        style={{
                          padding: 'var(--sp-2) var(--sp-3)',
                          background: 'var(--surface-sub)',
                          borderRadius: 'var(--r-ctl)',
                          fontSize: 'var(--text-xs)',
                          opacity: 0.5,
                        }}
                      >
                        {idx + 1}. {step}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Participating Groups */}
              <div style={{ marginBottom: 'var(--sp-5)' }}>
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-45)' }}>Participating Groups</h4>
                {groups.length > 0 ? (
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th><button className="th">Group</button></th>
                          <th><button className="th th--static">Task</button></th>
                          <th><button className="th th--static">Joined At</button></th>
                          <th><button className="th th--num">Response Latency</button></th>
                          <th><button className="th th--static">Status</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((g: any) => (
                          <tr key={g.id || g.name}>
                            <td className="td--name">{g.name}</td>
                            <td>{g.role || '—'}</td>
                            <td className="td--id" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
                              {g.joined_at ? new Date(g.joined_at).toLocaleTimeString() : '—'}
                            </td>
                            <td className="td--num" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
                              {g.response_latency_minutes != null ? `${g.response_latency_minutes.toFixed(1)}m` : '—'}
                            </td>
                            <td>
                              <span className="pill pill--ok">
                                <i></i> {g.status || 'active'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: 'var(--ink-45)', fontSize: 'var(--text-sm)' }}>No groups have joined this incident yet.</p>
                )}
              </div>

              {/* Parent/Child Ticket Tree */}
              <div>
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-45)' }}>Related Tickets</h4>
                {childTickets.length > 0 ? (
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th><button className="th">Ticket</button></th>
                          <th><button className="th th--static">Type</button></th>
                          <th><button className="th th--static">Status</button></th>
                          <th><button className="th">Assignee</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeIncident.parent_ticket && (
                          <tr style={{ background: 'var(--surface-sub)' }}>
                            <td className="td--id" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{activeIncident.parent_ticket.number || activeIncident.parent_ticket.id}</td>
                            <td>
                              <span className="pill pill--warn">
                                <i></i> Parent
                              </span>
                            </td>
                            <td>{activeIncident.parent_ticket.status || '—'}</td>
                            <td className="td--name">{activeIncident.parent_ticket.assignee || '—'}</td>
                          </tr>
                        )}
                        {childTickets.map((t: any) => (
                          <tr key={t.id}>
                            <td className="td--id" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{t.number || t.id}</td>
                            <td>
                              <span className="pill">
                                <i></i> Child
                              </span>
                            </td>
                            <td>{t.status || '—'}</td>
                            <td className="td--name">{t.assignee || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: 'var(--ink-45)', fontSize: 'var(--text-sm)' }}>No related tickets linked.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notification Composer (Modal) */}
        {composerOpen && activeIncident && (
          <>
            <div className="scrim is-on" onClick={() => setComposerOpen(false)} />
            <div className="drawer is-on" style={{ width: 640 }} role="dialog" aria-label="Compose notification">
              <div className="drawer__head">
                <h2 style={{ flex: 1 }}>Compose Notification</h2>
                <button className="ibtn" onClick={() => setComposerOpen(false)} aria-label="Close drawer">✕</button>
              </div>

              <div className="drawer__body">
              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label>Audience</label>
                <select
                  value={audience}
                  onChange={e => setAudience(e.target.value as any)}
                >
                  <option value="leadership">Leadership</option>
                  <option value="technical">Technical</option>
                  <option value="all_staff">All Staff</option>
                </select>
              </div>

              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label>Notification Body</label>
                <textarea
                  value={bodyOverride ?? buildNotificationBody()}
                  onChange={(e) => setBodyOverride(e.target.value)}
                  style={{
                    minHeight: 280,
                    fontFamily: 'var(--mono)',
                    lineHeight: 1.6,
                  }}
                />
                <button
                  className="btn btn--secondary btn--sm"
                  style={{ marginTop: 'var(--sp-2)' }}
                  disabled={regenerateNotification.isPending}
                  onClick={() => regenerateNotification.mutate()}
                >
                  {regenerateNotification.isPending ? 'Regenerating…' : 'Regenerate'}
                </button>
              </div>

              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label>Override Impact Statement</label>
                <textarea
                  value={impactOverride}
                  onChange={e => setImpactOverride(e.target.value)}
                  placeholder="Leave blank to use auto-populated value..."
                  style={{ minHeight: 60 }}
                />
              </div>

              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label>Override Workaround</label>
                <textarea
                  value={workaroundOverride}
                  onChange={e => setWorkaroundOverride(e.target.value)}
                  placeholder="Leave blank to use auto-populated value..."
                  style={{ minHeight: 60 }}
                />
              </div>

              <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                <label>Override Status</label>
                <input
                  type="text"
                  value={statusOverride}
                  onChange={e => setStatusOverride(e.target.value)}
                  placeholder="Leave blank to use auto-populated value..."
                />
              </div>

              <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <div>
                  <p className="notice__body">
                    Bedrock may smooth prose only — no facts, numbers, or names are generated.
                  </p>
                </div>
              </div>

              <button
                className="btn btn--primary"
                style={{ width: '100%', padding: 'var(--sp-3) var(--sp-4)' }}
                disabled={sendNotification.isPending}
                onClick={() => {
                  if (!activeIncident) return;
                  sendNotification.mutate({
                    incidentId: activeIncident.id,
                    body: bodyOverride ?? buildNotificationBody(),
                    audience,
                  });
                }}
              >
                {sendNotification.isPending ? 'Sending...' : 'Send Notification'}
              </button>

              {sendNotification.isSuccess && (
                <div className="notice notice--ok" style={{ marginTop: 'var(--sp-3)' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <div>
                    <p className="notice__body">Notification sent — 15-minute clock stopped.</p>
                  </div>
                </div>
              )}
              </div>
            </div>
          </>
        )}

        {/* MIM Performance */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Performance</p>
              <h2 className="box__title">Time to First Notification</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">Target: <b>{targetMinutes} min</b></span>
            </div>
          </div>
          <div className="box__body">
            {perfChartData.length > 0 ? (
              <>
                <div style={{ height: 240, marginBottom: 'var(--sp-4)' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={perfChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="minutes" radius={[4, 4, 0, 0]}>
                        {perfChartData.map((entry: any, index: number) => (
                          <Cell key={index} fill={entry.over ? 'var(--risk)' : 'var(--ok)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Target line reference */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--ok)', borderRadius: 'var(--r-xs)' }} />
                    <span style={{ color: 'var(--ink-45)' }}>Under target</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, background: 'var(--risk)', borderRadius: 'var(--r-xs)' }} />
                    <span style={{ color: 'var(--ink-45)' }}>Over target</span>
                  </span>
                </div>
              </>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">No performance data yet</h3>
                <p className="state__body">
                  Declare and resolve incidents to populate metrics.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Repeat Incidents by Service */}
        {repeatEntries.length > 0 && (
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Patterns</p>
                <h2 className="box__title">Repeat Incidents by Service</h2>
              </div>
            </div>
            <div className="box__body box__body--flush">
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th><button className="th">Service</button></th>
                      <th><button className="th th--num">Occurrences</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {repeatEntries.map(([service, count]) => (
                      <tr key={service}>
                        <td className="td--name">{service}</td>
                        <td className="td--num">
                          <span className={`pill pill--${count > 3 ? 'risk' : count > 2 ? 'warn' : 'ok'}`}>
                            <i></i> {count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
